import React from 'react';

import type { CustomOfferFormValues } from '@/typings/formValues';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form';

import { Box, Checkbox, FormControlLabel, FormGroup, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

type Props = {
  atiTicketingDisabled: boolean;
  control: Control<CustomOfferFormValues>;
  errors: FieldErrors<CustomOfferFormValues>;
  isCruiseRefare: boolean;
  pricingDisabled: boolean;
  register: UseFormRegister<CustomOfferFormValues>;
};

export default function CruisePostPurchaseSummary({
  atiTicketingDisabled,
  control,
  errors,
  isCruiseRefare,
  pricingDisabled,
  register,
}: Props) {
  return (
    <>
      <Box mt={2}>
        <Typography variant="h6">Summary</Typography>
      </Box>
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField
            {...register('payment_metadata.cruiseline_fee')}
            helperText={errors.payment_metadata?.cruiseline_fee?.message}
            error={!!errors.payment_metadata?.cruiseline_fee}
            disabled={pricingDisabled}
            label="Cruiseline Fee"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            required
            fullWidth
            data-testid="cruiseline-fee"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField
            {...register('payment_metadata.cruiseline_additional_charges')}
            helperText={errors.payment_metadata?.cruiseline_additional_charges?.message}
            error={!!errors.payment_metadata?.cruiseline_additional_charges}
            disabled={pricingDisabled}
            label="Cruiseline Additional Charges"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            required
            fullWidth
            data-testid="cruiseline-additional-charges"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField
            {...register('payment_metadata.le_fee')}
            helperText={errors.payment_metadata?.le_fee?.message}
            error={!!errors.payment_metadata?.le_fee}
            disabled={pricingDisabled}
            label="LE Fee"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            required
            fullWidth
            data-testid="cruise-le-fee"
          />
        </Grid>
        {isCruiseRefare && (
          <>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                {...register('payment_metadata.refare_new_fare')}
                helperText={errors.payment_metadata?.refare_new_fare?.message}
                error={!!errors.payment_metadata?.refare_new_fare}
                disabled={pricingDisabled}
                label="New Fare"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                required
                fullWidth
                data-testid="refare-new-fare"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                {...register('payment_metadata.refare_new_tax_and_surcharges')}
                helperText={errors.payment_metadata?.refare_new_tax_and_surcharges?.message}
                error={!!errors.payment_metadata?.refare_new_tax_and_surcharges}
                disabled={pricingDisabled}
                label="New Tax & Surcharges"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                required
                fullWidth
                data-testid="refare-new-tax-and-surcharges"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                {...register('payment_metadata.refare_deposit_fee_already_paid')}
                helperText={errors.payment_metadata?.refare_deposit_fee_already_paid?.message}
                error={!!errors.payment_metadata?.refare_deposit_fee_already_paid}
                disabled={pricingDisabled}
                label="Deposit / Fee Already Paid"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                required
                fullWidth
                data-testid="refare-deposit-fee-already-paid"
              />
            </Grid>
          </>
        )}
      </Grid>
      <Box mt={2}>
        <FormGroup row>
          <Controller
            name="sentToAtiTicketing"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value ?? false}
                    disabled={atiTicketingDisabled}
                    data-testid="sent-to-ati-ticketing"
                  />
                }
                label="Sent to ATI ticketing"
              />
            )}
          />
        </FormGroup>
      </Box>
    </>
  );
}
